// Utility functions for message page

import { useUnifiedDate, type TimestampInput } from '@/composables/date/useUnifiedDate'

// SMS 문자 제한 계산 함수 (실제 SMS 앱과 동일한 방식)
export const getSmsCharacterCount = (
  text: string,
  forceUnicode: boolean = true // 기본값을 한글 모드로 설정
): { count: number; isUnicode: boolean; maxChars: number; smsCount: number } => {
  if (!text) {
    // 빈 텍스트일 때도 한글 기준(70자)으로 표시
    return { count: 0, isUnicode: true, maxChars: 70, smsCount: 0 }
  }

  // GSM 7비트 기본 문자셋 (160자)
  const gsm7bitBasic =
    /^[A-Za-z0-9\s@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#$%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà]*$/

  // GSM 7비트 확장 문자셋 (2자로 계산)
  const gsm7bitExtended = /[{}[\]\\|~^€]/

  let isUnicode = forceUnicode // 강제 유니코드 모드 적용
  let charCount = 0

  // 강제 유니코드 모드가 아닐 때만 문자 검사
  if (!forceUnicode) {
    for (let i = 0; i < text.length; i++) {
      const char = text[i]

      if (char === '\n') {
        // 줄바꿈 문자도 1자로 계산 (SMS에서도 줄바꿈 유지)
        charCount += 1
      } else if (gsm7bitBasic.test(char)) {
        // 기본 GSM 7비트 문자
        charCount += 1
      } else if (gsm7bitExtended.test(char)) {
        // 확장 GSM 7비트 문자 (2자로 계산)
        charCount += 2
      } else {
        // 유니코드 문자 (한글, 이모지 등)
        isUnicode = true
        charCount += 1
      }
    }
  } else {
    // 강제 유니코드 모드일 때는 모든 문자를 1자로 계산
    charCount = text.length
  }

  let maxChars: number
  let smsCount: number

  if (isUnicode) {
    // 유니코드 (한글): 70자 단문, 67자씩 장문
    if (charCount <= 70) {
      maxChars = 70
      smsCount = charCount > 0 ? 1 : 0
    } else {
      smsCount = Math.ceil(charCount / 67)
      maxChars = 70 // 단문 기준 최대값 유지
    }
  } else {
    // GSM 7비트: 160자 단문, 153자씩 장문
    if (charCount <= 160) {
      maxChars = 160
      smsCount = charCount > 0 ? 1 : 0
    } else {
      smsCount = Math.ceil(charCount / 153)
      maxChars = 160 // 단문 기준 최대값 유지
    }
  }

  return {
    count: charCount,
    isUnicode,
    maxChars,
    smsCount,
  }
}

// Date/Time formatting functions
export function formatTime(timestamp: TimestampInput): string {
  const { toDate, isToday, isYesterday, getDaysDiff, formatSimpleTime } = useUnifiedDate()

  const date = toDate(timestamp)
  if (!date) return ''

  const now = new Date()
  const timeStr = formatSimpleTime(timestamp)

  if (isToday(timestamp)) {
    // 오늘 - 시간만 표시
    return timeStr
  } else if (isYesterday(timestamp)) {
    // 어제 - 어제 + 시간
    return `어제 ${timeStr}`
  } else {
    const diffDays = getDaysDiff(now, date)

    if (diffDays < 7) {
      // 일주일 이내 - 날짜 + 시간
      return `${diffDays}일 전 ${timeStr}`
    } else {
      // 7일 이상 - 날짜 + 시간
      return `${date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })} ${timeStr}`
    }
  }
}

export function formatDate(timestamp: TimestampInput): string {
  const { toDate, isToday, isYesterday, getDaysDiff } = useUnifiedDate()

  const date = toDate(timestamp)
  if (!date) return ''

  if (isToday(timestamp)) {
    return '오늘'
  } else if (isYesterday(timestamp)) {
    return '어제'
  } else {
    const now = new Date()
    const diffDays = getDaysDiff(now, date)

    if (diffDays === 2) {
      return '그제'
    } else {
      // 3일 이상 차이나면 M월 D일 요일 형식으로 표시
      return date.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' })
    }
  }
}

// Phone number formatting functions (통합 유틸리티 사용)
export { normalizePhoneDigits, formatPhoneByDigits, formatPhoneString } from '@/utils/phone'

/**
 * 리터럴 \n을 실제 줄바꿈 문자로 정규화
 *
 * SMS 등에서 "\n" 리터럴 문자열(백슬래시 + n)로 저장된 경우를
 * 실제 줄바꿈 문자로 변환합니다.
 *
 * @example
 * normalizeLineBreaks("안녕\\n하세요") // "안녕\n하세요"
 */
export function normalizeLineBreaks(text: string): string {
  if (!text) return ''
  return text.replace(/\\n/g, '\n')
}
